<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show" :items="['Volume','Value']" outlined dense hide-details v-model="show"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Wine period summary', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import {add,format} from 'date-fns';
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsWinesPeriodSummary',
  mixins: [printHtml],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      show: 'Volume',
      loading: true,
      wines: [],
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    totals() {
      return this.wines.reduce(({start,end}, wine) => (
        {start:start + wine.totalsStart[this.show], end:end + wine.totalsEnd[this.show]}
      ), {start:0,end:0});
    },
    sites() {
      return this.$store.getters.sites(true, {B263:true,B265:true});
    }
  },
  async created() {
    this.$emit('name', 'Wines period summary')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
  },
  methods: {
    async refresh() {
      this.loading = true;
      const wines = [];
      const end = [];
      const parameters = this.siteId ? [{name:'siteId', value:this.siteId}] : [];
      await Promise.all([
        this.$lazyListLoad(wines, 'wines2', 'id,name,volume,value', 100, [], [...parameters, {name:'asof', value:format(add(new Date(this.from+'T23:59:59'), {days:-1}),'yyyy-MM-dd HH:mm:ss')}]),
        this.$lazyListLoad(end, 'wines2', 'id,volume,value', 100, [], [...parameters, {name:'asof', value:this.to+' 23:59:59'}])
      ]);
      wines.forEach(wine => this.$set(wine, 'totalsStart', {Volume: wine.volume, Value:wine.value}));
      end.forEach(endWine => {
        const wine = wines.find(wine => wine.id === endWine.id);
        this.$set(wine, 'totalsEnd', {Volume: endWine.volume, Value:endWine.value});
      });
      this.wines = wines;
      this.loading = false;
    },

    report() {
      const lines = [
                  `<style>
                    @page {size:portrait}
                    .reports-wines-summary {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-wines-summary th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-wines-summary .spacer {height:18px}
                    .reports-wines-summary td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-wines-summary .title {font-size:18px;text-transform:uppercase}
                    .reports-wines-summary .sup-title {font-weight:normal;font-size:22px}
                    .reports-wines-summary .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .reports-wines-summary .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-wines-summary .text-left {text-align:left}
                    .reports-wines-summary .text-right {text-align:right}
                    .reports-wines-summary .font-weight-bold {font-weight:bold}
                    .reports-wines-summary .total-row {font-weight:bold}
                    .reports-wines-summary .capitalize {text-transform: capitalize}
                  </style>
                   <table class="reports-wines-summary">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.$store.getters.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">Wines period summary</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>

                        <tr class="header">
                          <th width="40%" class="text-left">Wine</th>
                          <th width="20%" class="text-right">Start ${this.show}</th>
                          <th width="20%" class="text-right">Change ${this.show}</th>
                          <th width="20%" class="text-right">End ${this.show}</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];
      for (const wine of this.wines) {
        lines.push(`<tr>`);
        lines.push(`<td>${wine.name}</td>`);
        lines.push(`<td class="text-right">${this.number(wine.totalsStart[this.show])}</td>`);
        lines.push(`<td class="text-right">${this.number(wine.totalsEnd[this.show]-wine.totalsStart[this.show])}</td>`);
        lines.push(`<td class="text-right">${this.number(wine.totalsEnd[this.show])}</td>`);
      }
      lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'">
                    <td class="text-right">Total</td>
                    <td class="text-right">${this.number(this.totals.start)}</td>
                    <td class="text-right">${this.number(this.totals.end-this.totals.start)}</td>
                    <td class="text-right">${this.number(this.totals.end)}</td>
                  </tr>`);
      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },
    number(num, csv=false) {
      if (this.show === 'Value')
        return `${csv ? '' : '$'}${this.$number(num, 2)}`;
      return `${this.$number(num, 3)}${csv ? '' : ' litres'}`;
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,`,
                      `"Wines period summary",,,`,
                      `"${this.dateDisplay}",,,`,
                      `"Wine","Start ${this.show}","Change ${this.show}","End ${this.show}"`
                    ];
      for (const wine of this.wines)
        lines.push(`"${wine.name}","${this.number(wine.totalsStart[this.show], true)}","${this.number(wine.totalsEnd[this.show]-wine.totalsStart[this.show], true)}","${this.number(wine.totalsEnd[this.show], true)}"`);
      lines.push(`"Total","${this.number(this.totals.start, true)}","${this.number(this.totals.end-this.totals.start, true)}","${this.number(this.totals.end, true)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Wines period summary.csv');
    }
  }
}
</script>